/**
 * 循環訂單「預約變更」卡片內就地展開詳情的狀態機（純 reducer，用法類似 Redux reducer）。
 *
 * 用法：呼叫端（薄殼 hook useCyclePurchaseEffectDetails.js）呼叫 reduce(state, event, payload) 取得 nextState，
 * state 長相是 { expandedIds, loadingIds, detailMap, tokens }，四個 map 都以 effectID 為 key：
 * - expandedIds：{ [effectID]: true }，目前展開中的變更單。
 * - loadingIds：{ [effectID]: true }，目前該筆詳情請求 in-flight。
 * - detailMap：{ [effectID]: data }，已成功取得的詳情快取（收合不清除，僅 CLEAR 才清除）。
 * - tokens：{ [effectID]: number }，每筆各自獨立的請求版本號，CLEAR 時遞增，用來讓「刪除後晚到的回應」失效。
 *
 * 設計原則（釘死，勿破壞，詳見 spec D8）：
 * - 純函式，不能有任何 side effect，不 import react、useAxios 等——外殼只負責讀 state、發請求、餵事件進來。
 * - 只有單一的「請求已 settle」事件 LOAD_RESOLVED，攜帶 { effectID, token, data }。
 *   成功／失敗的分岔完全由 reducer 判斷，呼叫端不得先判定再傳結果進來。
 * - LOAD_RESOLVED 依序執行：① 驗 token（與該 effectID 當前 token 不符 → 整個事件丟棄，狀態完全不變）
 *   ② 驗內容（data?.effectID === effectID 為真才算成功）③ 才決定寫入或走失敗路徑。
 *   順序不可顛倒——token 過期且 data 為 falsy 時，要走「丟棄」而非「失敗路徑」。
 * - token 為 per-effectID 獨立版本號，不可用單一全域版本號，否則某筆的 CLEAR 會誤殺其他筆正在飛的回應。
 * - 遇到不合法或缺 effectID 的事件一律回傳原 state（no-op），絕對不能 throw。
 *
 * ⚠️ effectID 型別要求（呼叫端必讀）：
 * LOAD_RESOLVED 的內容驗證是嚴格比對 data.effectID === effectID，而四個 map 是普通物件、
 * key 會被 JS 強制字串化。因此 payload 的 effectID 一律要傳**後端回傳的原始數值**
 * （例如 record.effectID），不可從 Object.keys(loadingIds) 之類的 map key 反查後傳入——
 * 那會拿到字串 "123"，與 data.effectID 的數字 123 比對失敗，導致每次展開都被靜默判定為失敗。
 */

// Events
export const EVENTS = {
    REQUEST_EXPAND: 'REQUEST_EXPAND',
    LOAD_RESOLVED: 'LOAD_RESOLVED',
    COLLAPSE: 'COLLAPSE',
    CLEAR: 'CLEAR',
};

/** 初始 state：四個空 map，皆以 effectID 為 key。 */
export const INIT = Object.freeze({
    expandedIds: Object.freeze({}),
    loadingIds: Object.freeze({}),
    detailMap: Object.freeze({}),
    tokens: Object.freeze({}),
});

/** 讀取指定 effectID 目前的請求 token，尚未有紀錄時預設為 0。 */
const getToken = (state, effectID) => state.tokens[effectID] || 0;

/** 回傳指定 key 已移除的新 map（copy-on-write，不動原物件）。 */
const withoutKey = (map, key) => {
    if (!Object.prototype.hasOwnProperty.call(map, key)) return map;
    const next = { ...map };
    delete next[key];
    return next;
};

/**
 * 純 reducer：reduce(state, event, payload?) → nextState。
 * @param {object} state 目前 state（至少含 expandedIds、loadingIds、detailMap、tokens）
 * @param {string} event event 名（見 EVENTS）
 * @param {object} [payload] event 附帶資料，至少含 effectID
 * @returns {object} nextState
 */
export const reduce = (state, event, payload = {}) => {
    // 防禦：外殼理應永遠傳入合法 state；若缺，退回初始（不 throw）
    const current = state || INIT;
    const { effectID } = payload;

    // 所有事件（含 CLEAR）都以 effectID 為必要條件；缺少一律 no-op，不 throw
    if (!effectID) return current;

    switch (event) {
        case EVENTS.REQUEST_EXPAND: {
            // 已在載入中或已展開：忽略，避免重複觸發（快速連點的最後一道防線，配合 hook 的 inFlightIdsRef）
            if (current.loadingIds[effectID] || current.expandedIds[effectID]) return current;

            // 已有快取：直接展開，不進載入中、不發請求
            if (Object.prototype.hasOwnProperty.call(current.detailMap, effectID)) {
                return {
                    ...current,
                    expandedIds: { ...current.expandedIds, [effectID]: true },
                };
            }

            // 無快取：進入載入中，交由外殼發請求
            return {
                ...current,
                loadingIds: { ...current.loadingIds, [effectID]: true },
            };
        }

        case EVENTS.LOAD_RESOLVED: {
            const { token, data } = payload;

            // ① 驗 token：與當前 token 不符 → 整個事件丟棄，狀態完全不變（防已刪除筆的晚到回應）
            if (getToken(current, effectID) !== token) return current;

            // ② 驗內容：data?.effectID === effectID 為真才算成功
            const isSuccess = Boolean(data) && data.effectID === effectID;

            if (isSuccess) {
                return {
                    ...current,
                    loadingIds: withoutKey(current.loadingIds, effectID),
                    expandedIds: { ...current.expandedIds, [effectID]: true },
                    detailMap: { ...current.detailMap, [effectID]: data },
                };
            }

            // ③ 失敗路徑：離開載入中，不加入已展開、不寫入快取
            return {
                ...current,
                loadingIds: withoutKey(current.loadingIds, effectID),
            };
        }

        case EVENTS.COLLAPSE: {
            if (!current.expandedIds[effectID]) return current;
            // 快取保留，只移除展開狀態
            return {
                ...current,
                expandedIds: withoutKey(current.expandedIds, effectID),
            };
        }

        case EVENTS.CLEAR: {
            // 同時移除展開／載入中／快取三處，並遞增該筆 token 使 in-flight 回應失效
            return {
                ...current,
                expandedIds: withoutKey(current.expandedIds, effectID),
                loadingIds: withoutKey(current.loadingIds, effectID),
                detailMap: withoutKey(current.detailMap, effectID),
                tokens: { ...current.tokens, [effectID]: getToken(current, effectID) + 1 },
            };
        }

        default:
            return current;
    }
};

export default reduce;
